// Configurações da aplicação

const joinUrl = (base, path) => {
  const normalizedBase = String(base || '').replace(/\/+$/, '')
  const normalizedPath = String(path || '').replace(/^\/+/, '')
  return `${normalizedBase}/${normalizedPath}`
}

const withTrailingSlash = (url) => {
  const s = String(url || '').trim()
  if (!s) return ''
  return s.endsWith('/') ? s : `${s}/`
}

/** Origem pública do SPA: dev = localhost, build = sistema.backlog.app.br — override com VITE_PUBLIC_ORIGIN */
const publicOrigin = withTrailingSlash(
  import.meta.env.VITE_PUBLIC_ORIGIN ||
    (import.meta.env.DEV ? 'http://localhost/' : 'https://sistema.backlog.app.br/')
)

const fileServerUrl = withTrailingSlash(
  import.meta.env.VITE_FILE_SERVER_URL || publicOrigin
)

const whatsGatewayUrl = import.meta.env.VITE_WHATSAPP_GATEWAY_URL
  ? String(import.meta.env.VITE_WHATSAPP_GATEWAY_URL).replace(/\/+$/, '')
  : joinUrl(publicOrigin.replace(/\/+$/, ''), 'whatsapp-api')

/** API JAX-RS: VITE_BACKEND_URL no .env; em dev, se vazio, usa /api (proxy do Vite). Em build, defina .env.production. */
const backendUrl = (
  import.meta.env.VITE_BACKEND_URL || (import.meta.env.DEV ? '/api' : '')
).replace(/\/+$/, '')

/** Hosts de arquivos antigos no banco — reescreve para fileServerUrl atual */
const LEGACY_FILE_HOSTS = ['backlog.oneway.tec.br']
const DEV_FILE_HOSTS = ['localhost', '127.0.0.1']

export const config = {
  publicOrigin,
  backendUrl,
  fileServerUrl,
  whatsGatewayUrl,
}

if (!import.meta.env.VITE_BACKEND_URL && !import.meta.env.DEV) {
  console.warn(
    '[config] VITE_BACKEND_URL não definido. Em produção, defina em .env.production antes do build.'
  )
}

console.log('🔧 Configurações carregadas:', {
  publicOrigin: config.publicOrigin,
  backendUrl: config.backendUrl || '(vazio)',
  fileServerUrl: config.fileServerUrl,
  whatsGatewayUrl: config.whatsGatewayUrl,
  env: {
    VITE_BACKEND_URL: import.meta.env.VITE_BACKEND_URL,
    VITE_PUBLIC_ORIGIN: import.meta.env.VITE_PUBLIC_ORIGIN,
    VITE_FILE_SERVER_URL: import.meta.env.VITE_FILE_SERVER_URL,
    VITE_WHATSAPP_GATEWAY_URL: import.meta.env.VITE_WHATSAPP_GATEWAY_URL,
  },
})

// Configurações de upload
config.upload = {
  maxFileSize: parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 5 * 1024 * 1024, // 5MB
  allowedTypes: import.meta.env.VITE_ALLOWED_FILE_TYPES?.split(',') || ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  cropSize: {
    width: 300,
    height: 300,
  },
}

// Configurações de tema
config.theme = {
  defaultTheme: 'dark',
  colors: {
    primary: '#5865F2',
    secondary: '#57F287',
    accent: '#FEE75C',
    danger: '#ED4245',
    warning: '#E67E22',
    info: '#95A5A6',
    purple: '#9B59B6',
    fuchsia: '#EB459E',
  },
}

export { joinUrl }

/** Base da API alinhada à origem do browser (SPA em sistema.* → /backlog-api no mesmo host). */
export const getEffectiveBackendUrl = () => {
  const backend = config.backendUrl
  if (!backend || typeof window === 'undefined' || import.meta.env.DEV) {
    return backend
  }
  try {
    const pageOrigin = window.location.origin
    const parsed = new URL(backend.startsWith('http') ? backend : `https://${backend}`)
    if (pageOrigin !== parsed.origin && parsed.hostname.endsWith('backlog.app.br')) {
      const ctx = parsed.pathname.replace(/\/$/, '')
      return `${pageOrigin}${ctx}`
    }
  } catch {
    /* keep backend */
  }
  return backend
}

/**
 * Base absoluta da API (http/https) para exibição e integrações externas.
 * Em dev com proxy /api → http://localhost:8080/backlog-api (ou VITE_BACKEND_URL).
 */
export const getAbsoluteBackendUrl = () => {
  const explicit = import.meta.env.VITE_BACKEND_URL?.trim()
  if (explicit && (explicit.startsWith('http://') || explicit.startsWith('https://'))) {
    return explicit.replace(/\/+$/, '')
  }

  if (typeof window === 'undefined') {
    const fallback = config.backendUrl || ''
    if (fallback.startsWith('http://') || fallback.startsWith('https://')) {
      return fallback.replace(/\/+$/, '')
    }
    return import.meta.env.DEV ? 'http://localhost:8080/backlog-api' : fallback.replace(/\/+$/, '')
  }

  const effective = getEffectiveBackendUrl()
  if (!effective) {
    return import.meta.env.DEV ? 'http://localhost:8080/backlog-api' : window.location.origin
  }

  if (effective.startsWith('http://') || effective.startsWith('https://')) {
    return effective.replace(/\/+$/, '')
  }

  if (effective.startsWith('/')) {
    if (import.meta.env.DEV && effective === '/api') {
      return 'http://localhost:8080/backlog-api'
    }
    return `${window.location.origin}${effective}`.replace(/\/+$/, '')
  }

  return `${window.location.origin}/${effective}`.replace(/\/+$/, '')
}

const alignWsBaseToPageOrigin = (wsBase) => {
  if (!wsBase || typeof window === 'undefined' || import.meta.env.DEV) {
    return wsBase
  }
  try {
    const pageOrigin = window.location.origin
    const wsOrigin = new URL(wsBase).origin
    if (pageOrigin !== wsOrigin && pageOrigin.includes('backlog.app.br')) {
      return `${pageOrigin}/backlog-api/ws/`
    }
  } catch {
    /* keep wsBase */
  }
  return wsBase
}

/** WebSocket Kanban: VITE_WS_URL ou mesma origem efetiva da API + /ws/kanban/{workspaceId} */
export const buildKanbanWebSocketUrl = (workspaceId, token) => {
  if (!workspaceId || !token) return ''

  const wsId = encodeURIComponent(String(workspaceId))
  const wsToken = encodeURIComponent(token)

  let explicitWs = import.meta.env.VITE_WS_URL?.trim()
  if (explicitWs) {
    explicitWs = alignWsBaseToPageOrigin(explicitWs)
    const base = explicitWs.replace(/\/+$/, '')
    return `${base}/kanban/${wsId}?token=${wsToken}`
  }

  const backend = getEffectiveBackendUrl()
  if (!backend) return ''

  // backend relativo (/api no Vite) — não usar `http://${backend}` (vira host "api")
  let u
  if (typeof window !== 'undefined' && backend.startsWith('/')) {
    u = new URL(backend, window.location.origin)
  } else if (backend.startsWith('http://') || backend.startsWith('https://')) {
    u = new URL(backend)
  } else if (typeof window !== 'undefined') {
    u = new URL(backend, window.location.origin)
  } else {
    u = new URL(backend, 'http://localhost:8080/backlog-api')
  }

  const wsProto = u.protocol === 'https:' ? 'wss:' : 'ws:'
  const port = u.port ? `:${u.port}` : ''
  const ctx = u.pathname.replace(/\/$/, '')
  return `${wsProto}//${u.hostname}${port}${ctx}/ws/kanban/${wsId}?token=${wsToken}`
}

const rewriteStoredFileUrl = (parsed) => {
  const host = (parsed.hostname || '').toLowerCase()
  if (LEGACY_FILE_HOSTS.includes(host)) {
    const relativePath = parsed.pathname.replace(/^\/+/, '')
    return joinUrl(config.fileServerUrl, relativePath)
  }
  if (DEV_FILE_HOSTS.includes(host)) {
    const m = parsed.pathname.match(/\/backlog-files\/(.+)/i)
    if (m?.[1]) {
      const rel = m[1].replace(/^\/+/, '')
      const base = String(config.fileServerUrl || '').replace(/\/+$/, '')
      if (base.toLowerCase().endsWith('/backlog-files')) {
        return `${base}/${rel}`
      }
      return joinUrl(config.fileServerUrl, `backlog-files/${rel}`)
    }
  }
  return null
}

// Função para obter URL completa do arquivo
/** URL pública de mídia WhatsApp (Apache backlog-files/whatsapp). */
const whatsappMediaFileNameFromRaw = (raw) => {
  const patterns = [
    /\/backlog-files\/whatsapp\/([^/?#;]+)/i,
    /\/whatsapp\/media\/([^/?#;]+)/i,
    /\/whatsapp\/([^/?#;]+\.[a-z0-9]{2,10})/i,
    /\/media\/([^/?#;]+)/i
  ]
  for (const re of patterns) {
    const m = raw.match(re)
    if (m?.[1]) return m[1].split(';')[0].trim()
  }
  if (!raw.includes('/') && /\.[a-z0-9]{2,10}/i.test(raw)) {
    return raw.split(';')[0].trim()
  }
  return null
}

const isAlreadyWhatsAppMediaUrl = (raw) => /\/backlog-files\/whatsapp\/[^/?#;]+/i.test(raw)

/** Monta URL pública: .../backlog-files/whatsapp/{fileName} */
const buildWhatsAppMediaPublicUrl = (fileName) => {
  const fname = String(fileName || '').trim().split(';')[0].trim()
  if (!fname) return ''
  const base = String(config.fileServerUrl || '').replace(/\/+$/, '')
  if (base.toLowerCase().endsWith('/backlog-files')) {
    return `${base}/whatsapp/${fname}`
  }
  return joinUrl(config.fileServerUrl, `backlog-files/whatsapp/${fname}`)
}

export const resolveWhatsAppMediaUrl = (urlOrFilename) => {
  if (!urlOrFilename) return ''
  const raw = String(urlOrFilename).trim().split(';')[0].trim()
  if (!raw) return ''
  if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw

  if (isAlreadyWhatsAppMediaUrl(raw)) {
    return raw
  }

  const fname = whatsappMediaFileNameFromRaw(raw)
  if (fname) {
    return buildWhatsAppMediaPublicUrl(fname)
  }

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    const rewritten = getFileUrl(raw)
    if (rewritten) return rewritten
    return raw
  }

  return getFileUrl(raw) || raw
}

export const getFileUrl = (filename) => {
  if (!filename) return null

  const raw = String(filename).trim()
  if (!raw) return null

  if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw

  if (raw.startsWith('/')) return raw

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const parsed = new URL(raw)
      const rewritten = rewriteStoredFileUrl(parsed)
      if (rewritten) return rewritten
      return raw
    } catch {
      return raw
    }
  }

  return joinUrl(config.fileServerUrl, raw)
}

/** URL de mídia (foto, anexo) — reescreve localhost e hosts legados. */
export const resolveMediaUrl = (urlOrFilename) => {
  if (!urlOrFilename) return ''
  return getFileUrl(urlOrFilename) || String(urlOrFilename).trim()
}

export const isValidFileType = (file) => {
  return config.upload.allowedTypes.includes(file.type)
}

export const isValidFileSize = (file) => {
  return file.size <= config.upload.maxFileSize
}
